import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { SKILL_QUERY_KEYS } from '@/lib/queryClient';
import { calculateGitHubEnhancedProfessionalSkills } from '../github/githubEnhancedProfessionalSkills';
import { calculateGitHubEnhancedPrivateSkills } from '../calculators/privateSkillsUtils';
import { projects } from '../../../../../db';
import { 
  calculateDurationInMonths,
  createSkillMaps,
  normalizeSkillFormat,
  addSkillToMap,
  normalizeSkillMaps,
  type SkillMaps 
} from '../calculators/skillCalculationUtils';
import { fetchGitHubLanguages, isOpenSourceGitHubRepo } from '../github/githubLanguages';

// Badge selection state type
export type BadgeSelection = {
  showProfessional: boolean;
  showPrivate: boolean;
};

// Default badge selection
const DEFAULT_BADGE_SELECTION: BadgeSelection = {
  showProfessional: true,
  showPrivate: false,
};

/**
 * Calculate combined professional and private skills with proper percentage normalization
 * This ensures skill percentages are calculated against the total duration of all projects
 */
async function calculateCombinedSkills(client: any): Promise<any> {
  const skillMaps = createSkillMaps();
  
  // Get all projects (professional and private)
  const allProjects = projects;
  
  // Calculate total duration across ALL projects for proper normalization
  let totalCombinedProjectMonths = 0;
  
  // Track enhanced projects to avoid duplicate GitHub calls
  const enhancedProjectsMap = new Map<number, any>();
  
  // Process all projects and enhance with GitHub data
  for (const project of allProjects) {
    let enhancedProject: any = { ...project };
    
    // Enhance with GitHub data if applicable
    if (isOpenSourceGitHubRepo(project.git)) {
      try {
        const githubLanguages = await fetchGitHubLanguages(project.git);
        if (githubLanguages.length > 0) {
          enhancedProject.githubLanguages = githubLanguages.map(lang => ({
            name: lang.name,
            weight: lang.weight
          }));
        }
      } catch (error) {
        console.warn(`Failed to enhance project ${project.title} with GitHub data:`, error);
      }
    }
    
    enhancedProjectsMap.set(project.id, enhancedProject);
    
    // Calculate project duration for normalization
    const projectDuration = calculateDurationInMonths(project.startDate, project.endDate);
    totalCombinedProjectMonths += projectDuration;
  }
  
  // Process all enhanced projects and build skill maps
  const enhancedProjects = Array.from(enhancedProjectsMap.values());
  for (const enhancedProject of enhancedProjects) {
    const projectDuration = calculateDurationInMonths(enhancedProject.startDate, enhancedProject.endDate);
    
    // Get normalized skill format
    let normalizedSkills = normalizeSkillFormat(enhancedProject);
    
    // Replace language skills with GitHub data if available
    if (enhancedProject.githubLanguages && enhancedProject.githubLanguages.length > 0) {
      normalizedSkills.language = enhancedProject.githubLanguages;
    }
    
    // Add skills to maps
    Object.entries(normalizedSkills).forEach(([category, skills]) => {
      const categoryKey = category as keyof SkillMaps;
      skills.forEach((skill: any) => {
        if (!skill.name || typeof skill.weight !== 'number') return;
        addSkillToMap(skillMaps, categoryKey, skill.name, projectDuration, skill.weight);
      });
    });
  }
  
  // Normalize against total combined duration
  const normalizedSkills = normalizeSkillMaps(skillMaps, totalCombinedProjectMonths);
  
  // Store enhanced projects in the cache for use by live examples
  client.setQueryData([SKILL_QUERY_KEYS.enhancedProjects], enhancedProjects);
  
  return normalizedSkills;
}

/**
 * Centralized skill state management using React Query cache
 * Provides single source of truth for badge selections and skill calculations
 */
export function useSkillsState() {
  const queryClient = useQueryClient();

  // Badge selection state
  const { data: badgeSelection = DEFAULT_BADGE_SELECTION } = useQuery({
    queryKey: [SKILL_QUERY_KEYS.badgeSelection],
    queryFn: () => DEFAULT_BADGE_SELECTION,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Professional skills calculation
  const { data: professionalSkills, isLoading: isLoadingProfessional } = useQuery({
    queryKey: [SKILL_QUERY_KEYS.professionalSkills, badgeSelection.showProfessional],
    queryFn: async () => {
      if (!badgeSelection.showProfessional) return [];
      return await calculateGitHubEnhancedProfessionalSkills();
    },
    enabled: badgeSelection.showProfessional,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Private skills calculation
  const { data: privateSkills, isLoading: isLoadingPrivate } = useQuery({
    queryKey: [SKILL_QUERY_KEYS.privateSkills, badgeSelection.showPrivate],
    queryFn: async () => {
      if (!badgeSelection.showPrivate) return [];
      return await calculateGitHubEnhancedPrivateSkills();
    },
    enabled: badgeSelection.showPrivate,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Combined skills calculation (depends on both professional and private)
  const { data: combinedSkills, isLoading: isLoadingCombined } = useQuery({
    queryKey: [
      SKILL_QUERY_KEYS.combinedSkills,
      badgeSelection.showProfessional,
      badgeSelection.showPrivate,
      professionalSkills,
      privateSkills,
    ],
    queryFn: async () => {
      return await calculateCombinedSkills(queryClient);
    },
    enabled: badgeSelection.showProfessional && badgeSelection.showPrivate,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update badge selection
  const updateBadgeSelection = useCallback((newSelection: Partial<BadgeSelection>) => {
    const updatedSelection = { ...badgeSelection, ...newSelection };
    
    queryClient.setQueryData([SKILL_QUERY_KEYS.badgeSelection], updatedSelection);
    
    // Invalidate dependent queries to trigger recalculation
    queryClient.invalidateQueries({ 
      queryKey: [SKILL_QUERY_KEYS.professionalSkills],
      exact: false 
    });
    queryClient.invalidateQueries({ 
      queryKey: [SKILL_QUERY_KEYS.privateSkills],
      exact: false 
    });
    queryClient.invalidateQueries({ 
      queryKey: [SKILL_QUERY_KEYS.combinedSkills],
      exact: false 
    });
  }, [queryClient, badgeSelection]);

  // Convenience methods for badge toggling
  const toggleProfessional = useCallback(() => {
    updateBadgeSelection({ showProfessional: !badgeSelection.showProfessional });
  }, [updateBadgeSelection, badgeSelection.showProfessional]);

  const togglePrivate = useCallback(() => {
    updateBadgeSelection({ showPrivate: !badgeSelection.showPrivate });
  }, [updateBadgeSelection, badgeSelection.showPrivate]);

  // Get current skills based on selection
  const currentSkills = useMemo(() => {
    if (badgeSelection.showProfessional && badgeSelection.showPrivate) {
      return combinedSkills || [];
    } else if (badgeSelection.showProfessional) {
      return professionalSkills || [];
    } else if (badgeSelection.showPrivate) {
      return privateSkills || [];
    }
    return [];
  }, [
    badgeSelection.showProfessional,
    badgeSelection.showPrivate,
    combinedSkills,
    professionalSkills,
    privateSkills,
  ]);

  // Loading state
  const isLoading = useMemo(() => {
    if (badgeSelection.showProfessional && badgeSelection.showPrivate) {
      return isLoadingCombined;
    } else if (badgeSelection.showProfessional) {
      return isLoadingProfessional;
    } else if (badgeSelection.showPrivate) {
      return isLoadingPrivate;
    }
    return false;
  }, [
    badgeSelection.showProfessional,
    badgeSelection.showPrivate,
    isLoadingCombined,
    isLoadingProfessional,
    isLoadingPrivate,
  ]);

  // Enhanced projects (for use by live examples)
  const { data: enhancedProjects = [] } = useQuery({
    queryKey: [SKILL_QUERY_KEYS.enhancedProjects],
    queryFn: () => {
      const data = queryClient.getQueryData([SKILL_QUERY_KEYS.enhancedProjects]);
      return Array.isArray(data) ? data : [];
    },
    enabled: false, // This is only populated by calculateCombinedSkills
  });

  return {
    // State
    badgeSelection,
    currentSkills,
    professionalSkills: professionalSkills || [],
    privateSkills: privateSkills || [],
    combinedSkills: combinedSkills || [],
    enhancedProjects: enhancedProjects || [],
    
    // Loading states
    isLoading,
    isLoadingProfessional,
    isLoadingPrivate,
    isLoadingCombined,
    
    // Actions
    updateBadgeSelection,
    toggleProfessional,
    togglePrivate,
  };
}